import Joi from "joi";
import { ParamError } from "../errors.js";
import { joiEdateType, joiStringArrayType } from "./custom-types.js";

/**
 * Parameter definition types
 */
 







































/**
 * Params class for parameter validation and type checking
 * Built on top of Args library with Joi validation
 */
export class Params {
    context; // Partial context during initialization
    params = {};
    paramSources = {};
    definitions = {};
    args;
    paramSetters = [];
    paramGetters = [];
    trackedParams = [];
    _currentModule = "script";
    /**
     * Resolved early in constructor so cleanup does not read params lazily.
     * One of: false (off) | "end" (print at exit) | "top" (print after init,
     * via context.showUsedParamsIfNeeded()) | "stop" (print after init, then
     * exit the process — also via context.showUsedParamsIfNeeded()).
     */
    _showUsedParamsMode = false;
    /** Guard so the dump prints at most once (top OR end, never both). */
    _usedParamsPrinted = false;

    constructor(context, options = {}) {
        // Context might be partial during initialization
        this.context = context;
        this.args = context.args;

        // Apply initial configuration
        if (Object.keys(options).length > 0) {
            this.configure(options);
        }

        // Resolve showUsedParams early (fail fast, consistent with "params figured in init")
        this._resolveShowUsedParams();

        if (context && typeof context.registerCleanup === "function") {
            context.registerCleanup((ctx) => {
                // Print at exit unless it was already printed at the top
                // (--showUsedParams=top via context.showUsedParamsIfNeeded()).
                // printUsedParams is idempotent, so "top" runs here are no-ops;
                // it also acts as a fallback if a "top" script never called
                // showUsedParamsIfNeeded().
                if (!ctx.params.getShowUsedParamsMode()) return;
                ctx.params.printUsedParams(ctx.logger);
            });
        }
    }

    /**
     * Resolve the --showUsedParams mode. The flag is intentionally dual-typed:
     *   (absent) / --no-showUsedParams / =false   -> false  (off)
     *   --showUsedParams / =true                   -> "end"  (print at exit)
     *   --showUsedParams=top                       -> "top"  (print after init)
     *   --showUsedParams=stop                      -> "stop" (print after init, then exit)
     * Read raw (uncoerced) from args so the string "top"/"stop" isn't forced to
     * a boolean, then track it under the "script" module for the dump itself.
     */
    _resolveShowUsedParams() {
        const raw = this.args.get("showUsedParams"); // also marks the key as used
        const source = this.args.getSource?.("showUsedParams") ?? "default";

        let mode = false;
        if (raw === undefined || raw === null) {
            mode = false;
        } else if (typeof raw === "string" && raw.trim().toLowerCase() === "top") {
            mode = "top";
        } else if (typeof raw === "string" && raw.trim().toLowerCase() === "stop") {
            mode = "stop";
        } else {
            const s = typeof raw === "string" ? raw.trim().toLowerCase() : raw;
            const falsey = s === false || s === "false" || s === "0" || s === "no" || s === "off";
            mode = falsey ? false : "end";
        }

        this._showUsedParamsMode = mode;
        this.trackParam("showUsedParams", "string", mode, raw === undefined ? "default" : source, "script");
        return mode;
    }

    /** Whether --showUsedParams was requested in any mode (truthy = on). */
    getShowUsedParams() {
        return this._showUsedParamsMode !== false;
    }

    /** Resolved mode: false | "end" | "top" | "stop". */
    getShowUsedParamsMode() {
        return this._showUsedParamsMode;
    }

    /**
     * Print the module-grouped list of figured params (the --showUsedParams
     * dump). Idempotent: only the first call prints, so callers can invoke it
     * at the top (long-running services) without double-printing at exit.
     */
    printUsedParams(logger) {
        if (this._usedParamsPrinted) return;
        const byModule = this.getFiguredByModule();
        const modules = Object.keys(byModule).sort();
        if (modules.length === 0) return;
        this._usedParamsPrinted = true;

        logger = logger ?? this.context?.logger ?? console;
        const hasHighlight = typeof logger.highlight === "function";
        logger.debug("[Params]: list of used params:");
        for (const mod of modules) {
            logger.debug(`  [${mod}]`);
            for (const [key, entry] of Object.entries(byModule[mod])) {
                const valueStr = JSON.stringify(entry.value);
                const display = (hasHighlight && entry.source !== "default")
                    ? logger.highlight(valueStr)
                    : valueStr;
                logger.debug(`    ${key}: ${display} (${entry.source})`);
            }
        }
    }

    /**
     * Configure parameters
     * Only parameters present in options are updated
     */
    configure(options) {
        for (const [k, v] of Object.entries(options)) {
            // TODO: opt values might be an object with definitions in it, so perhaps `this.set` should be used
            this.params[k] = v;
        }
    }

    /**
     * Initialize Params from context and CLI parameters
     * Note: Params is special - it's initialized early with partial context
     */
    static init(context, options) {
        return new Params(context, options || {});
    }

    /**
     * Track a parameter request for --stopAfter=init and --showUsedParams
     */
    trackParam(key, definition, value, source, moduleName) {
        this.trackedParams.push({
            key,
            definition,
            value,
            source,
            module: moduleName ?? this._currentModule,
        });
    }

    /**
     * Report a param value that a component RESOLVED ON ITS OWN — outside
     * Params.get() — e.g. discovered by combining its config files (the way
     * blueprints merge defaults/aggregator/feed data). Without this, every
     * key such a component probed for an override shows up in the
     * --showUsedParams dump as "undefined (default)"; reporting upgrades the
     * entry to the value the component actually works with.
     *
     * Attribution rules:
     *   - entries figured from an explicit input (cli/env/options/…) are left
     *     untouched — the component merely confirmed them, the origin stands;
     *   - "default" entries (Params had nothing) and earlier reports are
     *     replaced by the report;
     *   - keys never seen by Params are appended as new entries.
     * The LATEST report wins — a component may resolve the same key several
     * times with increasing specificity (e.g. a blueprint re-merged for a
     * concrete resource) and the dump should show what it settled on.
     * Later params.get() probes that find nothing ("default") never shadow a
     * reported value — see {@link getFiguredByModule}.
     *
     * @param {string} key
     * @param {*} value - the value the component actually uses
     * @param {string} [source="discovered"] - short origin label, e.g. "blueprint"
     * @param {string} [moduleName] - dump section; defaults to the current module
     */
    reportResolved(key, value, source = "discovered", moduleName) {
        const mod = moduleName ?? this._currentModule;
        const mine = this.trackedParams.filter((e) => e.key === key && e.module === mod);
        if (mine.some((e) => e.source !== "default" && !e.reported)) {
            return; // explicitly figured (cli/env/options/…) — that origin stands
        }
        // Replace default probes and earlier reports with this report.
        this.trackedParams = this.trackedParams.filter(
            (e) => !(e.key === key && e.module === mod)
        );
        this.trackedParams.push({
            key,
            definition: "reported",
            value,
            source,
            module: mod,
            reported: true,
        });
    }

    /**
     * Get all tracked parameters (for --stopAfter=init)
     */
    getTrackedParams() {
        return [...this.trackedParams];
    }

    /**
     * Get all figured parameters as a record (flat, last occurrence per key)
     * Returns all parameters that were collected during initialization,
     * whether from CLI args, options, or defaults
     */
    getAllFigured() {
        const result = {};
        for (const param of this.trackedParams) {
            result[param.key] = {
                value: param.value,
                source: param.source,
            };
        }
        return result;
    }

    /**
     * Get figured parameters grouped by module name.
     * Same param can appear in multiple modules (e.g. source, resource).
     * Last occurrence per key wins, EXCEPT that an empty probe — a
     * params.get() that found nothing ("default", undefined) — never shadows
     * a value reported via {@link reportResolved}: components probe for
     * overrides on every resolution cycle, and those misses say nothing about
     * the value the component actually uses.
     */
    getFiguredByModule() {
        const reported = new Set();
        for (const param of this.trackedParams) {
            if (param.reported) reported.add(`${param.module}\u0000${param.key}`);
        }
        const byModule = {};
        for (const param of this.trackedParams) {
            const mod = param.module;
            if (!byModule[mod]) byModule[mod] = {};
            if (
                !param.reported &&
                param.source === "default" &&
                reported.has(`${mod}\u0000${param.key}`)
            ) {
                continue;
            }
            byModule[mod][param.key] = { value: param.value, source: param.source };
        }
        return byModule;
    }

    /**
     * Clear tracked parameters
     */
    clearTrackedParams() {
        this.trackedParams = [];
    }

    /**
     * Assign a parameter definition
     */
    assignDefinition(key, definition) {
        if (this.definitions[key] && !definition) {
            return this.definitions[key];
        }

        let type;
        if (!definition) {
            type = Joi.string();
        } else if (Joi.isSchema(definition)) {
            type = definition;
        } else if (Joi.isSchema(definition.type)) {
            type = definition.type;
        } else if (typeof definition === "string") {
            type = this.toJoi(definition);
        } else if (typeof definition.type === "string") {
            type = this.toJoi(definition.type);
        } else if (!definition.type) {
            type = Joi.string();
        } else {
            type = Joi.string();
        }

        if (!this.definitions[key]) {
            this.definitions[key] = {};
        }
        this.definitions[key].type = type;

        if (definition && definition.values) {
            if (Array.isArray(definition.values)) {
                this.definitions[key].values = definition.values;
            }
        }
        return this.definitions[key];
    }

    /**
     * Convert string definition to Joi schema
     */
    toJoi(str) {
        let type;
        
        if (str.match(/^string|^text/i)) {
            type = Joi.string();
        } else if (str.match(/^number|^integer|^int/i)) {
            type = Joi.number();
        } else if (str.match(/^boolean|^bool/i)) {
            type = Joi.boolean();
        } else if (str.match(/^date/i)) {
            type = Joi.custom(joiEdateType);
        } else if (str.match(/^duration/i)) {
            type = Joi.string().isoDuration();
        } else if (str.match(/^array/i)) {
            let elementTypes = "string";
            const tmp = str.match(/\((.*)\)/);
            if (tmp && tmp[1].match(/string/i)) {
                elementTypes = "string";
            } else if (tmp && tmp[1].match(/number|integer|int/i)) {
                elementTypes = "number";
            } else if (tmp && tmp[1].match(/boolean|bool/i)) {
                elementTypes = "boolean";
            }
            type = Joi.custom(joiStringArrayType(elementTypes));
        } else {
            type = Joi.string();
        }

        // Handle default values
        const regexForDefault = /\bdefault\s+([^\s]+)/;
        const matchForDefault = str.match(regexForDefault);
        if (matchForDefault) {
            const defValObj = type.validate(matchForDefault[1]);
            if (defValObj.error) {
                throw new ParamError(`default value "${defValObj.value}" type mismatch`);
            }
            // Joi's default() automatically allows undefined and applies the default
            type = type.default(defValObj.value);
        } else if (str.match(/\s*required\s*/)) {
            type = type.required();
        } else {
            // If not required and no default, make it optional
            type = type.optional();
        }

        return type;
    }

    /**
     * Validate a value against a definition
     */
    validate(key, val, def) {
        // Convert null to undefined so Joi defaults can be applied
        // Joi's .default() only works with undefined, not null
        const normalizedVal = val === null ? undefined : val;
        
        // Pass current params as context to support cross-parameter references (e.g., @startTime+2h)
        // Use abortEarly: false to get all errors, and allowUnknown: false for strict validation
        const { value, error } = def.type.validate(normalizedVal, { 
            context: { params: this.params },
            abortEarly: false,
            allowUnknown: false,
        });
        if (error) {
            const errs = error.details.map((el) => el.message).join(", ");
            throw new ParamError(`"${key}" validation error: ${errs}`);
        }
        return value;
    }

    /**
     * Get a parameter value with validation
     */
    get(key, definition) {
        const def = this.assignDefinition(key, definition);
        let valFromGetters = undefined;
        
        // eslint-disable-next-line no-constant-condition
        if (def.volatile || true) {
            valFromGetters = this.runAllRegisteredGetters(key);
        }
        
        // Always call args.get() to mark the key as used, even if it doesn't exist
        const valFromArgs = this.args.get(key);
        const valFromParams = this.params[key];

        let source = "default";
        let value;

        if (valFromGetters !== undefined && valFromGetters !== null) {
            value = this.validate(key, valFromGetters, def);
            source = "options";
        } else if (valFromArgs !== undefined && valFromArgs !== null) {
            value = this.validate(key, valFromArgs, def);
            const argsSource = (this.args ).getSource?.(key);
            if (argsSource === "overrides") source = "options";
            else if (argsSource === "cli" || argsSource === "env" || argsSource === "config") source = argsSource;
            else if (argsSource === "default") source = "default";
            else source = "cli";
        } else if (valFromParams !== undefined && valFromParams !== null) {
            value = this.validate(key, valFromParams, def);
            source = this.paramSources[key] ?? "options";
        } else {
            value = this.validate(key, undefined, def);
            source = "default";
        }

        this.paramSources[key] = source;
        // Track parameter for --stopAfter=init and --showUsedParams
        this.trackParam(key, definition || "string", value, source);

        if (value !== undefined && def.values && !def.values.includes(value)) {
            throw new ParamError(`key ${key} should be one of ${def.values}`);
        }
        return value;
    }

    /**
     * Set a parameter value with validation
     */
    set(key, val, definition) {
        // TODO: check if there's a test for this:
        if (val && val.type && val.value) {
            definition = val;
            val = val.value;
        }
        this.assignDefinition(key, definition);

        if (!this.runAllRegisteredSetters(key, val)) {
            this.params[key] = val;
        }
    }

    /**
     * Get all parameters from definitions (main script).
     * Same as getAllForModule("script", defs). Processes left-to-right for cross-parameter references.
     * Libraries should use {@link getAllForModule} with an explicit module name (or {@link runWithModule}
     * around {@link get}) so --showUsedParams groups usage correctly.
     */
    getAll(defs) {
        return this.getAllForModule("script", defs);
    }

    /**
     * Get all parameters from definitions for a given module name.
     * Figured params are grouped by module when using --showUsedParams.
     * Processes parameters left-to-right to support cross-parameter references.
     * If moduleName is omitted, it is inferred from the caller's file path (directory name under src/).
     */
    getAllForModule(moduleNameOrDefs, defs) {
        let moduleName;
        let definitions;
        if (defs !== undefined) {
            moduleName = moduleNameOrDefs ;
            definitions = defs;
        } else {
            definitions = moduleNameOrDefs ;
            moduleName = this._inferModuleNameFromStack();
        }
        const prev = this._currentModule;
        this._currentModule = moduleName;
        try {
            const res = {};
            for (const [k, def] of Object.entries(definitions)) {
                const value = this.get(k, def);
                res[k] = value;
                if (value !== undefined) {
                    this.params[k] = value;
                }
            }
            return res;
        } finally {
            this._currentModule = prev;
        }
    }

    /**
     * Run a callback with {@link _currentModule} set so single {@link get} calls are tracked
     * under the same module (for --showUsedParams / getFiguredByModule).
     */
    runWithModule(moduleName, fn) {
        const prev = this._currentModule;
        this._currentModule = moduleName;
        try {
            return fn();
        } finally {
            this._currentModule = prev;
        }
    }

    /**
     * Async variant of {@link runWithModule} for modules that await params.get().
     */
    async runWithModuleAsync(moduleName, fn) {
        const prev = this._currentModule;
        this._currentModule = moduleName;
        try {
            return await fn();
        } finally {
            this._currentModule = prev;
        }
    }

    /**
     * Infer module name from call stack: first caller outside params/index gives path like .../src/<moduleName>/...
     */
    _inferModuleNameFromStack() {
        const stack = new Error().stack;
        if (!stack) return "script";
        const lines = stack.split("\n");
        const paramsIndexPath = "params" + (typeof process !== "undefined" && process.platform === "win32" ? "\\" : "/") + "index.";
        for (const line of lines) {
            const parenMatch = line.match(/\(([^)]+)\)/);
            if (!parenMatch) continue;
            const parts = parenMatch[1].split(":");
            if (parts.length < 3) continue;
            const path = parts.slice(0, -2).join(":").replace(/^file:\/\//, "");
            if (!path || path.includes(paramsIndexPath)) continue;
            const srcMatch = path.match(/[/\\]src[/\\]([^/\\]+)(?:[/\\]|$)/);
            if (srcMatch) return srcMatch[1];
        }
        return "script";
    }

    /**
     * Run all registered getters for a key
     */
    runAllRegisteredGetters(key) {
        let val = undefined;
        for (const getter of this.paramGetters) {
            val = getter(key, this.definitions[key]);
            if (val !== undefined && val !== null) {
                break;
            }
        }
        return val;
    }

    /**
     * Run all registered setters for a key
     */
    runAllRegisteredSetters(key, value) {
        let setterUsed = false;
        for (const setter of this.paramSetters) {
            setterUsed = setter(key, value);
            if (setterUsed) {
                break;
            }
        }
        return setterUsed;
    }

    /**
     * Register a parameter getter
     */
    registerParamGetter(fn) {
        this.paramGetters.push(fn);
    }

    /**
     * Register a parameter setter
     */
    registerParamSetter(fn) {
        this.paramSetters.push(fn);
    }
}

// Export custom types for external use
export { joiEdateType, joiStringArrayType };

